import { HttpClient } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Observable, Subscriber } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    ApiBanksResponse,
    ApiCardAdjustments,
    ApiCardAdjustmentsPayload,
    ApiCouponPayload,
    ApiCouponResponse, ApiListTicketless,
    ApiOrder,
    ApiOrders,
    ApiOrderTracking,
    ApiPaymentBundle,
    ApiPaymentMethods,
    ApiPaymentMethodsParams,
    ApiPaymentMode,
    ApiPromoCodePayload,
    ApiPromoCodeResponse,
    ApiRefundResponse,
    ApiReceipts,
    ApiOrderPoints,
    ApiZalandoReturnRequest,
} from './api-order.models';
import { OrderTransformer } from './api-order.transformer';

import { Invoices, OrderPartialGiftTicketData, Order, Orders, Receipts, OrderRefundData } from '@api/order/order.models';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { HttpRequestsService } from '@core/infrastructure/http-requests/http-requests.service';
import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import { DiscountDetails } from '@features/payment';

@Injectable({
    providedIn: 'root',
})
export class ApiOrderService {
    constructor(
        private readonly http: HttpClient,
        private readonly requestService: HttpRequestsService,
        @Inject(WINDOW) private readonly window: CommonWindow,
        private readonly platformService: CorePlatformService,
    ) { }

    public setCardAdjustments(
        storeId: string,
        orderId: string,
        langId: string,
        payload: ApiCardAdjustmentsPayload,
    ): Observable<ApiCardAdjustments> {
        const url: string = this.requestService.requestUrl(`order/store/${storeId}/order/${orderId}/checkout/card-adjustments`, langId);
        return this.http.post<ApiCardAdjustments>(url, payload, { withCredentials: true });
    }

    public setCardAdjustmentsItx(orderId: string, payload: ApiCardAdjustmentsPayload): Observable<DiscountDetails> {
        if (this.platformService.isBrowser()) {
            return new Observable((observer) => {
                const backup = this.window.inditex.iUserJSON;

                this.window.inditex.iUserJSON = {
                    ...this.window.inditex.iUserJSON,
                    shopCart: {
                        id: orderId,
                    },
                };

                const callback = (success, result): void => {
                    this.window.inditex.iUserJSON = backup;
                    this.cardDetailsCallbackHandler(success, result, observer);
                };

                const cardData = {
                    number: payload.paymentData[0].number,
                    cvv2: payload.paymentData[0].cvv2,
                    paymentMethodType: payload.paymentData[0].paymentMethodType,
                    paymentMethodKind: payload.paymentData[0].paymentMethodKind,
                    variant: payload.paymentData[0].variant,
                    balance: 0,
                };
                const options = {
                    captchaValue: payload.captchaValue,
                    captchaId: payload.captchaId,
                    cvv2: payload.paymentData[0].cvv2,
                    number: payload.paymentData[0].number,
                    privacyPolicyAccepted: true,
                };

                this.window.inditex.addPaymentCard(cardData, callback, options);
            });
        }
    }

    public unsetCardAdjustments(
        storeId: string,
        orderId: string,
        langId: string,
        payload: ApiCardAdjustmentsPayload,
    ): Observable<ApiCardAdjustments> {
        const url: string = this.requestService.requestUrl(`order/store/${storeId}/order/${orderId}/checkout/card-adjustments`, langId);
        return this.http.put<ApiCardAdjustments>(url, payload, { withCredentials: true });
    }

    public unsetCardAdjustmentsItx(
        orderId: string,
        index: number,
    ): Observable<DiscountDetails> {
        return new Observable((observer) => {
            const backup = this.window.inditex.iUserJSON;
            this.window.inditex.iUserJSON = {
                ...this.window.inditex.iUserJSON,
                shopCart: {
                    id: orderId,
                },
            };

            const callback = (success, result): void => {
                this.window.inditex.iUserJSON = backup;
                this.cardDetailsCallbackHandler(success, result, observer);
            };

            this.window.inditex.removePaymentCard(index, callback);
        });
    }

    public paymentMethods(params: ApiPaymentMethodsParams): Observable<ApiPaymentMethods> {
        // hay que cambiar el numero de pedido segun lo que haya ahora mismo
        const slug = `order/store/${params.storeId}/order/${params.orderId}/payment`;
        const urlParams = params.repay ? [{ key: 'repay', value: '1' }] : undefined;
        const url = this.requestService.requestUrl(slug, params.langId, urlParams, true, undefined, undefined, params.appId);
        return this.http.get<ApiPaymentMethods>(url, { withCredentials: true });
    }

    public addPromoCode(
        storeId: string,
        languageId: string,
        orderId: string,
        data: ApiPromoCodePayload,
    ): Observable<ApiPromoCodeResponse> {
        const slug = `order/store/${storeId}/order/${orderId}/promoCode`;
        const url = this.requestService.requestUrl(slug, languageId);
        return this.http.post<ApiPromoCodeResponse>(url, data, { withCredentials: true });
    }

    public deletePromoCode(
        storeId: string,
        languageId: string,
        orderId: string,
        data: ApiPromoCodePayload,
    ): Observable<ApiPromoCodeResponse> {
        const slug = `order/store/${storeId}/order/${orderId}/promoCode/${data.promoCode}`;
        const url = this.requestService.requestUrl(slug, languageId);
        return this.http.delete<ApiPromoCodeResponse>(url, { withCredentials: true });
    }

    public setUnboundPayment(
        storeId: string,
        langId: string,
        orderId: string,
        data: ApiPaymentBundle,
    ): Observable<Order> {
        const url = this.requestService.requestUrl(`order/store/${storeId}/order/${orderId}/checkout/unboundpayment`, langId);
        return this.http.post<Order>(url, data, { withCredentials: true });
    }

    public paymentModes(
        storeId: any,
        langId: string,
        orderId: string,
        policy: string,
        cardNumber: string,
        vatin: string,
        orderAmount?: number,
    ): Observable<ApiPaymentMode> {
        const url = this.requestService.requestUrl(`order/store/${storeId}/order/${orderId}/payment/${policy}/mode`, langId);
        const data = {
            number: cardNumber,
            vatin,
            amount: orderAmount || undefined,
        };
        return this.http.put<ApiPaymentMode>(url, data, { withCredentials: true });
    }

    public getOrderById(storeId: string, langId: string, orderId: number, _subOrders: boolean): Observable<Order> {
        const url = this.requestService.requestUrl(`order/store/${storeId}/order/${orderId}`, langId);

        return this.http
            .get<ApiOrder>(url, { withCredentials: true })
            .pipe(map((apiOrderData) => OrderTransformer.OrderFromApi(apiOrderData)));
    }

    public getOrderByIdGuest(storeId: string, langId: string, orderId: number, guestId: string): Observable<Order> {
        const url = this.requestService.requestUrl(`order/store/${storeId}/order-guest/${orderId}`, langId, [{ key: 'tokenGuest', value: guestId }]);
        return this.http
            .get<ApiOrder>(url)
            .pipe(map((apiOrderData) => OrderTransformer.OrderFromApi(apiOrderData)));
    }

    public getOrderPoints(storeId: string, langId: string, orderId: string): Observable<ApiOrderPoints> {
        const url = this.requestService.requestUrl(`user/store/${storeId}/order/${orderId}/points`, langId, null, false, '4');

        return this.http.get<ApiOrderPoints>(url, { withCredentials: true });
    }

    public getOrders(storeId: string, langId: string, index?: number): Observable<Orders> {
        let url = this.requestService.requestUrl(`order/store/${storeId}/order`, langId, [
            { key: 'howMany', value: '30' },
            { key: 't', value: Date.now().toString() },
        ]);
        if (index) {
            url = `${url}&index=${index}`;
        }

        return this.http
            .get<ApiOrders>(url, { withCredentials: true })
            .pipe(map((apiOrdersData) => OrderTransformer.OrdersFromApi(apiOrdersData)));
    }

    public getOrdersStore(
        userId: number,
        storeId: string,
        _langId: string,
        today: string,
        index = 0,
    ): Observable<Receipts> {
        const slug = `bam/receipt/store/${storeId}/user/${userId}/receipts`;
        const url = this.requestService.requestUrl(slug, null, [
            { key: 'fromDate', value: '1970-01-01' },
            { key: 'toDate', value: today },
            { key: 'offset', value: index.toString() },
            { key: 'limit', value: '1000' },
        ], false, '1');

        return this.http
            .get<ApiReceipts>(url, { withCredentials: true })
            .pipe(map((apiReceiptsData) => OrderTransformer.ReceiptsFromApi(apiReceiptsData)));
    }

    public addCoupon(storeId: string, orderId: string, data: ApiCouponPayload): Observable<ApiCouponResponse> {
        const slug = `order/store/${storeId}/order/${orderId}/couponCode`;
        const url = this.requestService.requestUrl(slug);
        return this.http.post<ApiCouponResponse>(url, data, { withCredentials: true });
    }

    public deleteCoupon(storeId: string, orderId: string, data: ApiCouponPayload): Observable<ApiCouponResponse> {
        const slug = `order/store/${storeId}/order/${orderId}/couponCode/${data.couponCode}?t=${Math.random()}`;
        const url = this.requestService.requestUrl(slug);
        return this.http.delete<ApiCouponResponse>(url, { withCredentials: true });
    }

    public getOrderPaymentMethodBanks(
        storeId: string,
        languageId: string,
        orderId: string,
        paymentMethod: string,
    ): Observable<any> {
        const slug = `order/store/${storeId}/order/${orderId}/paymentmethod/${paymentMethod}/banks`;
        const url = this.requestService.requestUrl(slug, languageId);
        return this.http.get<ApiBanksResponse>(url, { withCredentials: true });
    }

    public tracking(storeId: string, languageId: string, orderId: string) {
        const slug = `order/store/${storeId}/order/${orderId}/tracking`;
        const url = this.requestService.requestUrl(slug, languageId, null, null, '3');
        return this.http.get<ApiOrderTracking>(url, { withCredentials: true });
    }

    public zalandoReturn(
        marketPlaceOrderId: string,
        caseNumber: string,
        languageId: string,
    ): Observable<ApiZalandoReturnRequest> {
        const slug = `order/${marketPlaceOrderId}/return-request/label`;
        const url = this.requestService.requestUrl(slug, languageId, null, null);
        return this.http.post<ApiZalandoReturnRequest>(url, { caseNumber }, { withCredentials: true });
    }

    public getInvoices(storeId: string, languageId: string, index: number): Observable<Invoices> {
        const slug = `user/store/${storeId}/invoices`;
        const url = this.requestService.requestUrl(slug, languageId, [{
            key: 'index',
            value: index.toString(),
        }]);
        return this.http.get<any>(url, { withCredentials: true })
            .pipe(map((apiInvoices) => OrderTransformer.InvoicesFromApi(apiInvoices)));
    }

    public printEgui(orderId: string, languageId: string): Observable<any> {
        // Como el brandId siempre va a ser 11 porque es dutti no hace falta pasarlo como parametro
        const slug = `orders/1/financial/brand/11/order/${orderId}/egui/print`;
        const url = this.requestService.requestUrl(slug, languageId, null, null, '4');
        return this.http.post<any>(url, { withCredentials: true });
    }

    private cardDetailsCallbackHandler(success: boolean, result, observer: Subscriber<DiscountDetails>): void {
        if (success) {
            const [cardAdded] = result.slice(-1);
            const discountDetails: DiscountDetails = {
                saveCard: false,
                valid: true,
                values: {
                    ...cardAdded,
                },
            };
            observer.next(discountDetails);
            observer.complete();
        } else {
            observer.error({ key: result?.key, message: result?.description });
        }
    }

    public getPdfListTicketless(receiptUID: string, storeId: string, languageId: string) {
        const slug = `bam/receipt/${receiptUID}/store/${storeId}/detail/pdflist`;
        const url = this.requestService.requestUrl(slug, languageId, null, null, '1');
        return this.http.get<ApiListTicketless>(url, { withCredentials: true })
            .pipe(map((apiListTicketless) => OrderTransformer.ListTicketlessFromApi(apiListTicketless)));
    }

    public cancelOrder(orderId: number | string, storeId: string, languageId: string) {
        const slug = `order/store/${storeId}/order/${orderId}`;
        const url = this.requestService.requestUrl(slug, languageId);
        return this.http.delete<ApiOrder>(url, { withCredentials: true })
            .pipe(map((apiOrderData) => OrderTransformer.OrderFromApi(apiOrderData)));
    }

    public cancelGuestOrder(orderId: number | string, storeId: string, languageId: string, guestToken: string) {
        const slug = `order/store/${storeId}/order-guest/${orderId}`;
        const url = this.requestService.requestUrl(slug, languageId, [{
            key: 'guestToken',
            value: guestToken,
        }]);
        return this.http.delete<ApiOrder>(url, { withCredentials: true })
            .pipe(map((apiOrderData) => OrderTransformer.OrderFromApi(apiOrderData)));
    }

    public cancelSubOrder(parentId: number | string, suborderId: number | string, storeId: string, languageId: string) {
        const slug = `order/store/${storeId}/order/${parentId}/suborder/${suborderId}`;
        const url = this.requestService.requestUrl(slug, languageId);
        return this.http.delete<ApiOrder>(url, { withCredentials: true })
            .pipe(map((apiOrderData) => OrderTransformer.OrderFromApi(apiOrderData)));
    }

    public cancelGuestSubOrder(
        parentId: number | string,
        suborderId: number | string,
        storeId: string,
        languageId: string,
        guestToken: string,
    ) {
        const slug = `order/store/${storeId}/order/${parentId}/suborder-guest/${suborderId}`;
        const url = this.requestService.requestUrl(slug, languageId, [{
            key: 'guestToken',
            value: guestToken,
        }]);
        return this.http.delete<ApiOrder>(url, { withCredentials: true })
            .pipe(map((apiOrderData) => OrderTransformer.OrderFromApi(apiOrderData)));
    }

    public getStoreOrderDetail(
        receiptUID: string,
        QRCode: string,
        storeId: string,
        languageId: string,
    ) {
        const slug = `bam/receipt/${receiptUID}/store/${storeId}/detail/live`;
        const url = this.requestService.requestUrl(slug, languageId, [{
            key: 'qr-code',
            value: QRCode,
        }], false, '1');
        return this.http.get<any>(url, { withCredentials: true });
        // .pipe(map((apiOrderData) => OrderTransformer.OrderFromApi(apiOrderData)));
    }

    public orderRefund(storeId: string, orderId: number, data: OrderRefundData): Observable<ApiRefundResponse> {
        const slug = `order/store/${storeId}/order/${orderId}/refund`;
        const url = this.requestService.requestUrl(slug);
        return this.http.post<ApiRefundResponse>(url, data, { withCredentials: true });
    }

    public orderRefundGuest(
        storeId: string,
        orderId: number,
        data: OrderRefundData,
        tokenGuest: string,
    ): Observable<ApiRefundResponse> {
        const slug = `order/store/${storeId}/order/${orderId}/refund-guest`;
        const customParams = [{
            key: 'tokenGuest',
            value: tokenGuest,
        }];
        const url = this.requestService.requestUrl(slug, null, customParams);
        return this.http.post<ApiRefundResponse>(url, data, { withCredentials: true });
    }

    public orderPartialGiftTicket(
        storeId: string,
        orderId: number,
        data: OrderPartialGiftTicketData,
    ): Observable<ArrayBuffer> {
        const slug = `order/store/${storeId}/order/${orderId}/getOrderPartialGiftTicket`;
        const url = this.requestService.requestUrl(slug);
        return this.http.request(
            'PUT',
            url,
            {
                body: data,
                withCredentials: true,
                responseType: 'arraybuffer',
            },
        );
    }
}
